import { IRaterProps, IUnderwritingSocketOutput, RaterTab } from './types';
import {
  Submission,
  IUnderwritingQuote,
  RaterMode,
  RaterPolicyDetails,
  RaterUserQuotes,
} from '@converge/graphql';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from './data';
import { memo, useEffect } from 'react';
import { config, convergeLogger } from '@converge/config';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import { GetAuthToken } from '../../auth/auth-provider';
import { LoadingPageLayout } from '../../../presentation';
import { RaterContents } from './rater.contents';
import { formatDollarAmount } from '@converge/utils';
import { useConvergeSnackBar } from '../../../contexts';
import useDeepCompareEffect from 'use-deep-compare-effect';

export interface IRaterInnerProps extends IRaterProps {
  quote: IUnderwritingQuote;
  submission: Submission;
  userQuotes: RaterUserQuotes;
  isEditable: boolean;
  mode: RaterMode;
  policy?: RaterPolicyDetails;
}

export const RaterInner = memo(({ quote }: IRaterInnerProps) => {
  const { notify } = useConvergeSnackBar();
  const { actions, dispatch } = useRaterActionDispatchContext();

  const stateTab = useRaterStateContextSelector((state) => state?.tab);
  const stateQuote = useRaterStateContextSelector((state) => state?.quote);
  const stateMode = useRaterStateContextSelector((state) => state?.mode);
  const messagesToSend = useRaterStateContextSelector(
    (state) => state?.messagesToSend
  );

  const { sendMessage, readyState } = useWebSocket(
    config.getWebsocketUrl(),
    {
      onOpen: (event) => {
        convergeLogger.info(
          `WebSocket connected to ${config.getWebsocketUrl()} ${(
            event.timeStamp / 1000
          ).toFixed(2)} second(s) after ${window.location.href} was opened`,
          event
        );

        const authToken = GetAuthToken();
        if (authToken) {
          sendMessage(authToken);
        } else {
          convergeLogger.error(
            'No auth token available to connect to websocket'
          );
        }
      },
      onMessage: (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (cause) {
          console.warn(`Socket data cannot be parsed: ${cause}`);
          console.warn(cause);
          notify({
            message: `Unable to parse socket response: ${cause}`,
            variant: 'error',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          });
        }
        if (data) {
          dispatch(actions.receiveSocketMessage(data));

          if ('type' in data && data.type === 'coverage_rating_response') {
            const message = data as IUnderwritingSocketOutput;
            const previousPremium =
              (stateTab !== RaterTab.ExcessRating
                ? stateQuote.ratedPremium
                : stateQuote.excessConvergeRatedPremium) ?? 0;
            const newPremium =
              (stateTab !== RaterTab.ExcessRating
                ? message.results.ratedPremium
                : message.results.excess?.convergeRatedPremium) ?? 0;

            if (
              config.featureFlags.showRaterUpdateNotifications &&
              newPremium !== previousPremium
            ) {
              const message: string[] = [];

              if (stateTab === RaterTab.ExcessRating) {
                message.push('Excess rated premium');
              } else {
                message.push('Rated premium');
              }

              if ((stateQuote?.ratedPremium ?? 0) < newPremium) {
                message.push('increased');
              } else {
                message.push('decreased');
              }

              message.push(
                'by',
                formatDollarAmount(
                  Math.abs((previousPremium ?? 0) - (newPremium ?? 0))
                ),
                'from',
                formatDollarAmount(previousPremium),
                'to',
                formatDollarAmount(newPremium)
              );

              notify({
                preventDuplicate: true,
                message: message.join(' '),
                variant: 'info',
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              });
            }
          }
        }
      },
      shouldReconnect: () => {
        return true;
      },
      onError(event) {
        convergeLogger.error(`WebSocket error: ${event.type}`, event);
      },
      onClose(event) {
        dispatch(actions.onDisconnectFromSocket());

        if (event.wasClean) {
          convergeLogger.info(
            `WebSocket closed cleanly: ${event.code} ${event.reason}`,
            event
          );
        } else {
          convergeLogger.error(`WebSocket closed: ${event.type}`, event);
          notify({
            message: `The rater disconnected with code ${event.code}.`,
            variant: 'error',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          });
        }
      },
    },
    true
  );

  useDeepCompareEffect(() => {
    if (quote) {
      dispatch(actions.updateQuote(quote as IUnderwritingQuote));
    }
  }, [quote ?? {}]);

  useEffect(() => {
    if (readyState !== ReadyState.OPEN) {
      return;
    }

    for (const message of messagesToSend) {
      sendMessage(message.message);
      dispatch(actions.markMessageSent(message.id));
    }
  }, [actions, dispatch, readyState, sendMessage, messagesToSend]);

  if (!stateQuote && stateMode === RaterMode.CreateQuote) {
    return (
      <LoadingPageLayout
        title="Creating quote..."
        titleId="underwriting.quotes.creatingQuote.title"
        subtitle={'Please wait a moment'}
        subtitleId="underwriting.quotes.creatingQuote.subtitle"
        subtitleTooltip={'Please wait a moment'}
      />
    );
  }
  return <RaterContents />;
});
